import apiClient from "../helpers/apiClient";
import { format, isValid, parseISO } from "date-fns";

export const jobLocationService = {
  getJobLocations,
  getJobLocation,
  createJobLocation,
  updateJobLocation,
  deleteJobLocation,
  setJobLocationStatus,
};

function displayValue(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function formatJobLocationDate(value) {
  if (value === null || value === undefined || value === "") return "-";

  const date =
    value instanceof Date
      ? value
      : typeof value === "string"
        ? parseISO(value)
        : new Date(value);

  return isValid(date) ? format(date, "dd MMM yyyy") : "-";
}

function findJobLocationPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const wrapped = payload.job_location_info;
  if (Array.isArray(wrapped)) return wrapped;
  if (wrapped && typeof wrapped === "object") return [wrapped];
  if (payload.job_location_id !== undefined) return [payload];
  return [];
}

function mapJobLocation(raw = {}) {
  return {
    id: displayValue(raw.job_location_id),
    name: displayValue(raw.job_location_name),
    statusId: displayValue(raw.job_location_status_id),
    statusName: displayValue(raw.job_location_status_name),
    createdBy: displayValue(raw.encoded_by_name),
    createdAt: formatJobLocationDate(raw.encoded_date),
    lastChangedBy: displayValue(raw.last_changed_by_name),
    lastChangedAt: formatJobLocationDate(raw.last_changed_date),
  };
}

function getErrorMessage(error) {
  return error.response?.data?.msg || "Something went wrong";
}

function extractWriteResult(response) {
  const data = response?.data;
  const messageObject = Array.isArray(data?.result_status)
    ? data.result_status[0]
    : Array.isArray(data)
      ? data[0]
      : data;

  return {
    message:
      messageObject?.message_text ||
      messageObject?.Message_Text ||
      "Saved successfully.",
    refNo: messageObject?.message_ref_no ?? messageObject?.Message_Ref_No ?? null,
  };
}

async function handleRequest(request) {
  try {
    return await request();
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

async function handleWriteRequest(request) {
  try {
    const response = await request();
    return extractWriteResult(response);
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

function getJobLocations() {
  return handleRequest(async () => {
    const response = await apiClient.get("/hr/job-locations");
    return findJobLocationPayload(response?.data).map(mapJobLocation);
  });
}

function getJobLocation(id) {
  return handleRequest(async () => {
    const response = await apiClient.get(`/hr/job-locations/${id}`);
    const [jobLocation] = findJobLocationPayload(response?.data);
    return jobLocation ? mapJobLocation(jobLocation) : null;
  });
}

function createJobLocation(name) {
  return handleWriteRequest(() =>
    apiClient.post("/hr/job-locations", { job_location_name: name })
  );
}

function updateJobLocation(id, name, statusId) {
  return handleWriteRequest(() =>
    apiClient.put(`/hr/job-locations/${id}`, {
      job_location_name: name,
      job_location_status_id: statusId,
    })
  );
}

function deleteJobLocation(id) {
  return handleWriteRequest(() => apiClient.delete(`/hr/job-locations/${id}`));
}

function setJobLocationStatus(id, statusId) {
  return handleWriteRequest(() =>
    apiClient.patch(`/hr/job-locations/${id}/status`, {
      job_location_status_id: statusId,
    })
  );
}
